'use client'

import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Check,
  ChevronDown
} from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'

import { useMediaQuery } from '@/hooks/use-media-query'

import { MobileToolbarGroup, MobileToolbarItem } from './mobile-toolbar-group'
import { useToolbar } from './toolbar-provider'

export const AlignmentTooolbar = () => {
  const { editor } = useToolbar()
  const t = useTranslations('components.tiptap.toolbars.alignment')
  const isMobile = useMediaQuery('(max-width: 640px)')
  const handleAlign = (value: string) => {
    editor?.chain().focus().setTextAlign(value).run()
  }

  const currentTextAlign = () => {
    if (editor?.isActive({ textAlign: 'left' })) {
      return 'left'
    }
    if (editor?.isActive({ textAlign: 'center' })) {
      return 'center'
    }
    if (editor?.isActive({ textAlign: 'right' })) {
      return 'right'
    }
    if (editor?.isActive({ textAlign: 'justify' })) {
      return 'justify'
    }

    return 'left'
  }

  const alignmentOptions = [
    {
      icon: <AlignLeft className="h-4 w-4" />,
      name: t('left'),
      value: 'left'
    },
    {
      icon: <AlignCenter className="h-4 w-4" />,
      name: t('center'),
      value: 'center'
    },
    {
      icon: <AlignRight className="h-4 w-4" />,
      name: t('right'),
      value: 'right'
    },
    {
      icon: <AlignJustify className="h-4 w-4" />,
      name: t('justify'),
      value: 'justify'
    }
  ]

  const findIndex = (value: string) => {
    return alignmentOptions.findIndex((option) => option.value === value)
  }

  if (isMobile) {
    return (
      <MobileToolbarGroup
        label={
          alignmentOptions[findIndex(currentTextAlign())]?.name ?? t('left')
        }
      >
        {alignmentOptions.map((option, index) => (
          <MobileToolbarItem
            active={currentTextAlign() === option.value}
            key={index}
            onClick={() => handleAlign(option.value)}
          >
            <span className="mr-2">{option.icon}</span>
            {option.name}
          </MobileToolbarItem>
        ))}
      </MobileToolbarGroup>
    )
  }

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              className="h-8 w-max font-normal"
              size="sm"
              type="button"
              variant="ghost"
            >
              {alignmentOptions[findIndex(currentTextAlign())]?.icon}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('title')}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        loop
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
      >
        <DropdownMenuGroup className="w-40">
          {alignmentOptions.map((option, index) => (
            <DropdownMenuItem
              key={index}
              onSelect={() => {
                handleAlign(option.value)
              }}
            >
              <span className="mr-2">{option.icon}</span>
              {option.name}

              {option.value === currentTextAlign() && (
                <Check className="ml-auto h-4 w-4" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
